import React, { FC, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { BootstrapTable, TableHeaderColumn } from 'react-bootstrap-table';
import { Dealer, DealerId } from '@upstart/harmony/lib/legos/dealerTypes';
import { OutreachFormStatusList } from '@upstart/harmony/lib/legos/outreachTypes';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import ErrorMessage from '../../components/ErrorMessage';
import LoadingOverlay from '../../components/LoadingOverlay';
import { dealerRequest } from '../../redux/dealers/actions';
import { outreachFormsStatusRequest } from '../../redux/outreachForms/actions';
import { getTemplateListAugmentedWithData } from '../../util/outreachFormUtils';
import EditButton from './EditButton';
import { Dealers, OutreachFormState, OutreachFormsState } from './types';

interface MatchParams {
  dealerId: DealerId;
}

interface Props extends RouteComponentProps<MatchParams> {
  actions: {
    dealerRequest: ({ dealerId }: { dealerId: DealerId }) => void;
    outreachFormsStatusRequest: ({ dealerId }: { dealerId: DealerId }) => void;
  };
  dealer?: Dealer;
  outreachForms: OutreachFormStatusList;
  error?: string | null;
  isFetching?: boolean;
}

const OutreachForms: FC<Props> = ({
  actions,
  dealer,
  outreachForms,
  error,
  isFetching = false,
  match,
}: Props) => {
  const { dealerId }: { dealerId: DealerId } = match.params;

  useEffect(() => {
    /* Refreshing "Dealer" is necessary to maintain state when user does
     * something like "Open in New Tab" etc., otherwise DealerNav and such
     * will be rendered with dealer "null". */
    actions.dealerRequest({ dealerId });

    actions.outreachFormsStatusRequest({ dealerId });
  }, [actions, dealerId]);

  const actionFormatter = (_cell: object, outreachForm: OutreachFormState) => (
    <EditButton
      dealerId={outreachForm.dealerId}
      templateId={outreachForm.templateId}
    />
  );

  const data = useMemo(
    () =>
      dealerId && outreachForms
        ? getTemplateListAugmentedWithData(dealerId, outreachForms)
        : [],
    [dealerId, outreachForms],
  );

  if (!dealerId) {
    return null;
  }

  const count = data.length;

  return (
    <SidebarContainer
      title={dealer?.name || 'Outreach Forms'}
      logo={dealer?.logo}
    >
      <div className="dealers-table">
        <DealerNav />
        <div className="dealer-editor-container tabbed-page-container">
          {error && <ErrorMessage error={error} />}
          <LoadingOverlay active={isFetching}>
            <h3>
              {count} Item{count === 1 ? '' : 's'}
            </h3>
            <BootstrapTable
              hover
              condensed
              tableStyle={{ border: 'none' }}
              data={data}
            >
              <TableHeaderColumn dataField="name" key="templateId" isKey>
                Name
              </TableHeaderColumn>
              <TableHeaderColumn dataField="enabled">Enabled</TableHeaderColumn>
              <TableHeaderColumn dataFormat={actionFormatter} />
            </BootstrapTable>
          </LoadingOverlay>
        </div>
      </div>
    </SidebarContainer>
  );
};

const mapStateToProps = ({
  dealers,
  outreachForms,
}: {
  dealers?: Dealers;
  outreachForms: OutreachFormsState;
}) => ({
  dealer: dealers?.dealer,
  outreachForms: outreachForms.outreachForms,
  error: outreachForms?.error,
  isFetching: dealers?.fetching || outreachForms?.isFetching,
});

const mapDispatchToProps = (dispatch: Dispatch<{}>) => ({
  actions: bindActionCreators(
    { dealerRequest, outreachFormsStatusRequest },
    dispatch,
  ),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(OutreachForms),
);
